import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import { ref, get, update } from 'firebase/database';
import Button from '../ui/Button';
import {
  WorkspacePageError,
  WorkspacePageLoader,
} from '../ui/Workspace';
import UserList from './UserList';
import UserFilters from './UserFilters';
import UserForm from './UserForm';
import Modal from '../ui/Modal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const UsersPage = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ role: 'all', status: 'all', search: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const [usersSnapshot, studentsSnapshot, instructorsSnapshot] = await Promise.all([
        get(ref(db, 'users')),
        get(ref(db, 'students')),
        get(ref(db, 'instructors')),
      ]);

      const usersData = usersSnapshot.val() || {};
      const studentsData = studentsSnapshot.val() || {};
      const instructorsData = instructorsSnapshot.val() || {};
      const map = new Map();

      Object.entries(usersData).forEach(([id, user]) => {
        const uid = user.uid || id;
        map.set(uid, { id: uid, ...user, role: user.role || 'admin', status: user.status || 'active' });
      });
      Object.entries(studentsData).forEach(([id, student]) => {
        const uid = student.uid || id;
        if (!map.has(uid)) {
          map.set(uid, { id: uid, ...student, role: 'student', status: student.status || 'active' });
        }
      });
      Object.entries(instructorsData).forEach(([id, instructor]) => {
        const uid = instructor.uid || id;
        if (!map.has(uid)) {
          map.set(uid, { id: uid, ...instructor, role: instructor.role || 'instructor', status: instructor.status || 'active' });
        }
      });

      setUsers(Array.from(map.values()).map((u) => ({ ...u, role: u.role || 'student' })));
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Reset to page 1 whenever filters or page size change
  useEffect(() => { setPage(1); }, [filters, pageSize]);

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        if (filters.role !== 'all' && user.role !== filters.role) return false;
        if (filters.status !== 'all' && user.status !== filters.status) return false;
        if (filters.search) {
          const term = filters.search.toLowerCase();
          const name = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
          if (!name.includes(term) && !(user.email || '').toLowerCase().includes(term)) return false;
        }
        return true;
      }),
    [users, filters],
  );

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleAddUser = () => { setSelectedUser(null); setIsModalOpen(true); };
  const handleEditUser = (user) => { setSelectedUser(user); setIsModalOpen(true); };

  const handleToggleUserStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      const collections = ['users'];
      if (user.role === 'student') collections.push('students');
      else if (['instructor', 'demonstrator'].includes(user.role)) collections.push('instructors');

      const updates = {};
      collections.forEach((col) => {
        updates[`${col}/${user.id}/status`] = newStatus;
        updates[`${col}/${user.id}/updatedAt`] = new Date().toISOString();
        updates[`${col}/${user.id}/personalDetails/status`] = newStatus;
      });
      await update(ref(db), updates);

      try {
        const updateUserStatus = httpsCallable(functions, 'updateUserStatus');
        const result = await updateUserStatus({ uid: user.id, disabled: newStatus === 'inactive' });
        if (!result.data.success) throw new Error('Failed to update Firebase Auth status');
      } catch (authError) {
        console.warn('Could not update Firebase Auth status:', authError);
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? { ...u, status: newStatus, personalDetails: { ...u.personalDetails, status: newStatus }, updatedAt: new Date().toISOString() }
            : u,
        ),
      );
      setError(null);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(`Failed to ${newStatus === 'active' ? 'activate' : 'deactivate'} user`);
      fetchUsers();
    }
  };

  if (loading) return <WorkspacePageLoader />;
  if (error) return <WorkspacePageError>{error}</WorkspacePageError>;

  return (
    <div className="mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-1">
      {/* Page header */}
      <header className="rounded-lg border border-stone-200 bg-white px-4 py-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              Workspace
            </p>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-stone-950">
              Users
            </h1>
          </div>
          {currentUser.role === 'admin' && (
            <Button type="button" onClick={handleAddUser}>Add User</Button>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <UserStat label="Total Users" value={users.length} />
          <UserStat label="Filtered" value={filteredUsers.length} />
          <UserStat label="Page" value={`${currentPage} / ${totalPages}`} />
          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Per page</p>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="mt-0.5 w-full border-0 bg-transparent p-0 text-base font-semibold text-stone-950 focus:outline-none focus:ring-0"
            >
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </header>

      {/* Filters */}
      <UserFilters filters={filters} setFilters={setFilters} />

      {/* Table */}
      <UserList
        users={pagedUsers}
        onEdit={handleEditUser}
        onToggleStatus={handleToggleUserStatus}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={filteredUsers.length}
        pageSize={pageSize}
        onPageChange={setPage}
      />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <UserForm
          user={selectedUser}
          onClose={() => setIsModalOpen(false)}
          onSuccess={fetchUsers}
        />
      </Modal>
    </div>
  );
};

const UserStat = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    <p className="mt-0.5 truncate text-base font-semibold text-stone-950">{value}</p>
  </div>
);

export default UsersPage;
